import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../layouts';
import { PhoneInput } from '../components/form';
import { formatPhoneNumberForAPI } from '../utils/formatters';

const ForgotPasswordPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('+61');
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!phoneNumber) {
      setError('Please enter your phone number');
      return;
    }
    
    // Format phone number for API
    const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber, dialCode);
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      // Call forgotPassword function from AuthContext
      const result = await forgotPassword(formattedPhoneNumber);
      
      if (result) {
        const deliveryMethod = dialCode === '+1' ? 'email address' : 'phone number';
        setSuccess(`Password reset code has been sent to your ${deliveryMethod}`);
        
        // Redirect to reset password page after a short delay
        setTimeout(() => {
          navigate('/reset-password', {
            state: { phoneNumber: phoneNumber, dialCode: dialCode }
          });
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to initiate password reset. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Forgot Password</h1>
        
        {error && (
          <div className="bg-racing-red-light text-racing-red p-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="bg-racing-green-light text-racing-green p-3 rounded mb-4">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="phoneNumber" className="block text-gray-700 mb-2">
              Phone Number
            </label>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              onCountryCodeChange={setDialCode}
              defaultDialCode="+61"
              error={phoneError}
              setError={setPhoneError}
              required
            />
            {dialCode === '+1' && (
              <p className="text-xs text-gray-500 mt-1">
                A verification code will be sent to your email address.
              </p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-racing-blue text-white py-2 px-4 rounded hover:bg-racing-blue-dark transition ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Remember your password?{' '}
            <Link to="/login" className="text-racing-blue hover:underline">
              Login
            </Link>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ForgotPasswordPage;
